import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Stack, Select, MenuItem, Button, Divider, Tooltip, Chip, Menu,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import type { BillLineItem, BillStatus } from '../data/types';
import { BILL_STATUSES } from '../data/types';
import { BRAND, STATUS_TONE, getStatusTone, FI_ORANGE } from '../theme/tokens';
import {
  BTN_STATUS_GREEN, BTN_STANDARD, BTN_GHOST_DOC, BTN_GHOST_DANGER, BTN_PURPLE,
  PO_STICKY_LIFECYCLE_SX, PO_STICKY_LIFECYCLE_PUSH, PO_STICKY_LIFECYCLE_TRAIL,
  TABLE_HEAD_CELL, TABLE_HEAD_SUPPLIER,
} from '../theme/poButtonTokens';
import { LineCodeSelect, type LineCodeOption } from '../components/LineCodeSelect';
import {
  LineTableColumnSettings, loadColumnVisibility, saveColumnVisibility,
} from '../components/LineTableColumnSettings';
import { TableCellInput } from '../components/TableCellInput';
import { BILL_LINE_COLUMN_DEFS } from '../config/salesDocumentLineColumns';
import { autoMatchOrCreate, peekNextInternalCode } from '../utils/codeMatching';
import { StatusChip } from '../components/StatusChip';
import { BillEditForm } from '../components/BillEditForm';
import { InfoableButton, type ButtonInfo } from '../components/ImportResolutionBadge';
import { AiImportBanner } from '../components/AiImportBanner';
import { AttachFileZone } from '../components/AttachFileZone';
import { setPendingBillImportFile } from '../utils/billImportHandoff';
import { buildAccountingExport } from '../utils/accountingExport';

const BILL_DOC_MAX_WIDTH = 1280;
const BILL_MASTHEAD_EDIT_INSET = 2;
const TEMPLATES = ['Default Template', 'Supplier Invoice', 'Project Bill'];
const BILL_TABS = ['Jobs & inventory', 'History'] as const;

const FOOTER_BTN_INFO: Record<
  | 'preview' | 'close' | 'pdf' | 'exportXero' | 'reconcile'
  | 'findPo' | 'generatePoFromBill' | 'generateBlankPo'
  | 'submitApproval' | 'approve' | 'approveSend' | 'sendPayment' | 'resend' | 'markPaid' | 'deleteBill',
  ButtonInfo
> = {
  approveSend: {
    title: 'Approve & send for payment',
    description: 'Approve the bill and push it to your accounting product (Xero / MYOB / QuickBooks) as a bill awaiting payment. Use once the bill and its PO line up.',
    example: 'BILL-2026-019 matches PO-4575 — Approve & send to Xero creates the payable there.',
  },
  sendPayment: {
    title: 'Send for payment',
    description: 'Push this already-approved bill to your accounting product as a bill awaiting payment.',
    example: 'Send BILL-2026-019 to Xero for payment.',
  },
  resend: {
    title: 'Re-send to accounting',
    description: 'Push this bill to your accounting product again (e.g. after an edit).',
    example: 'Re-send BILL-2026-019 to Xero after fixing a code.',
  },
  preview: {
    title: 'Preview',
    description: 'Read-only supplier invoice view — letterhead, line items, and totals as the supplier sent them.',
    example: 'Preview BILL-2026-019 before exporting to Xero.',
  },
  close: {
    title: 'Close',
    description: 'Return to the Bills list. Edits in the masthead save automatically.',
    example: 'Close after checking BILL-2026-019 totals against the linked PO.',
  },
  pdf: {
    title: 'PDF',
    description: 'Print or save this bill as a PDF using your browser print dialog.',
    example: 'PDF BILL-2026-019 and file it in the job folder.',
  },
  exportXero: {
    title: 'Export to Xero',
    description: 'Push this approved bill to Xero as an AP supplier invoice.',
    example: 'Export BILL-2026-019 to Xero after approval.',
  },
  reconcile: {
    title: 'Reconcile',
    description: 'Opens the line-by-line reconciliation view — match bill lines to the linked PO, resolve code variances, and sync receipt quantities.',
    example: 'Reconcile BILL-2026-019 against PO-4575 when the supplier invoice arrives.',
  },
  findPo: {
    title: '+ Find PO',
    description: 'Links an existing purchase order to this bill when the PO was raised separately.',
    example: 'BILL-2026-019 was scanned from email; + Find PO links it to PO-4575.',
  },
  generatePoFromBill: {
    title: '+ Generate PO from Bill',
    description: 'Creates a draft PO pre-filled from this bill\u2019s lines and links it back.',
    example: 'The invoice arrived before the PO was raised. Generate PO from Bill drafts PO-0041 from BILL-2026-019 lines.',
  },
  generateBlankPo: {
    title: '+ Generate Blank PO',
    description: 'Creates an empty draft PO linked to this bill for manual line entry.',
    example: 'Generate Blank PO when the bill covers freight not yet on any order.',
  },
  submitApproval: {
    title: 'Submit for Approval',
    description: 'Send this draft bill for internal approval before payment.',
    example: 'Submit BILL-2026-019 for manager sign-off.',
  },
  approve: {
    title: 'Approve',
    description: 'Approve this bill for payment processing. The status verb — strongest action on the page.',
    example: 'Approve BILL-2026-019 after matching totals to PO-4575.',
  },
  markPaid: {
    title: 'Mark as Paid',
    description: 'Record that this bill has been paid in full.',
    example: 'Mark BILL-2026-019 as Paid after the EFT clears.',
  },
  deleteBill: {
    title: 'Delete',
    description: 'Remove this draft bill permanently. Only available while status is Draft.',
    example: 'Delete BILL-2026-030 raised against the wrong supplier.',
  },
};

function fmtDate(d?: string): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return d; }
}

const fmt = (n: number) => `$${n.toFixed(2)}`;

const DISCLOSURE_ROW_MB = 0.35;
const DISCLOSURE_ACTION_GAP = 1.25;

/** Tiered cascade headers — mirrors POPrintPage disclosureLevelSx (v1.50.1). */
const disclosureLevelSx = (level: 1 | 2 | 3) => {
  const accents = { 1: BRAND, 2: '#6a4fc4', 3: '#9b8cc8' } as const;
  const bgs = { 1: '#f3edfc', 2: '#f8f5ff', 3: '#faf9fd' } as const;
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    width: '100%',
    mx: -0.5,
    px: 1.25,
    py: level === 1 ? 0.9 : level === 2 ? 0.8 : 0.7,
    ml: level === 1 ? 0 : level === 2 ? 0.5 : 1,
    borderRadius: 1.5,
    border: '1px solid #e8e2f4',
    borderLeft: `3px solid ${accents[level]}`,
    backgroundColor: bgs[level],
    cursor: 'pointer',
    transition: 'background-color 0.12s ease',
    '&:hover': { backgroundColor: level === 1 ? '#ebe3f9' : level === 2 ? '#f0ebfa' : '#f5f2fa' },
  } as const;
};

const disclosureSummarySx = {
  ml: 'auto',
  flexShrink: 0,
  textAlign: 'right' as const,
  fontSize: '0.72rem',
  color: '#666',
  fontVariantNumeric: 'tabular-nums' as const,
  lineHeight: 1.35,
  pr: 0.25,
};

const BILL_LINE_COL_IDS = BILL_LINE_COLUMN_DEFS.map(c => c.id);

export const BillDocumentView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    bills, billLineItems, suppliers, purchaseOrders, products, supplierPriceBook,
    updateBill, deleteBillImport, setBillLineItems,
    trackSupplierCodes, internalCodePrefix, addProduct, addSupplierCodeMap, addSupplierPriceBookEntries,
    lookupSupplierCode, lookupSupplierPriceBookEntry, accountingProduct,
  } = useData();

  const bill = bills.find(b => b.id === id);
  const supplier = bill ? suppliers.find(s => s.id === bill.supplierId) : null;
  const linkedPo = bill?.linkedPoId ? purchaseOrders.find(po => po.id === bill.linkedPoId) : null;
  const lines = bill ? (billLineItems[bill.id] ?? []) : [];

  const [colVisible, setColVisible] = useState<Record<string, boolean>>(() =>
    loadColumnVisibility('bill-line-columns-v1', BILL_LINE_COL_IDS));
  const toggleCol = (id: string) => {
    setColVisible(prev => {
      const next = { ...prev, [id]: prev[id] === false };
      saveColumnVisibility('bill-line-columns-v1', next);
      return next;
    });
  };
  const showCol = (id: string) => colVisible[id] !== false;

  const bookForSupplier = useMemo(
    () => (bill ? supplierPriceBook.filter(e => e.supplierId === bill.supplierId) : []),
    [supplierPriceBook, bill],
  );
  const supplierOptions: LineCodeOption[] = useMemo(
    () => bookForSupplier.map(e => ({
      key: e.id,
      code: e.supplierCode,
      description: e.description,
      meta: e.unitPrice ? `$${e.unitPrice.toFixed(2)}` : undefined,
      linkedCode: e.internalCode ?? null,
    })),
    [bookForSupplier],
  );
  const internalOptions: LineCodeOption[] = useMemo(
    () => products.map(p => ({
      key: p.code,
      code: p.code,
      description: p.description,
      meta: p.purchasePrice ? `$${p.purchasePrice.toFixed(2)}` : undefined,
    })),
    [products],
  );

  // Persist a full replacement line list AND keep the bill's totals in sync
  // (subtotal / GST / total) — PO parity: edits recompute money immediately.
  const commitLines = (next: BillLineItem[]) => {
    if (!bill) return;
    setBillLineItems(bill.id, next);
    const subtotal = next.reduce((s, l) => s + l.lineTotal, 0);
    const gst = next.reduce((s, l) => s + l.gstAmount, 0);
    updateBill(bill.id, { subtotalExGst: subtotal, totalGst: gst, totalIncGst: subtotal + gst });
  };

  const updateBillLine = (lineId: number, patch: Partial<BillLineItem>) => {
    if (!bill) return;
    const next = lines.map(l => {
      if (l.id !== lineId) return l;
      const merged = { ...l, ...patch };
      // Money edits: recompute the derived line amounts.
      if ('qty' in patch || 'unitPrice' in patch) {
        merged.lineTotal = merged.qty * merged.unitPrice;
        merged.gstAmount = merged.gstTreatment === 'inc' ? 0 : merged.lineTotal * 0.1;
      }
      return merged;
    });
    commitLines(next);
  };

  // ── Add / delete lines manually (PO parity — v1.52.2) ─────────────────────
  const handleAddLineItem = () => {
    if (!bill) return;
    const nextId = lines.reduce((m, l) => Math.max(m, l.id), 0) + 1;
    commitLines([
      ...lines,
      {
        id: nextId, supplierCode: '', internalCode: '', lineType: 'product',
        codeMatch: 'none', description: '', qty: 1, unitPrice: 0,
        gstTreatment: 'ex', gstAmount: 0, lineTotal: 0,
      },
    ]);
  };

  const handleDeleteLine = (lineId: number) => {
    commitLines(lines.filter(l => l.id !== lineId));
  };

  const slugSupplierCode = (description: string) => {
    const slug = description.trim().slice(0, 20).replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return (slug || 'ITEM').toUpperCase();
  };

  const handleInternalAutoCreate = (line: BillLineItem) => {
    if (!bill || !supplier) return;
    const sc = (line.supplierCode || '').trim();
    if (trackSupplierCodes && !sc) return;
    if (!line.description?.trim()) return;

    const supplierCodeForMatch = sc || slugSupplierCode(line.description);
    const entry = lookupSupplierPriceBookEntry(bill.supplierId, supplierCodeForMatch);
    const result = autoMatchOrCreate({
      supplierCode: supplierCodeForMatch,
      description: line.description,
      supplierName: supplier.name,
      products,
      priceBookEntry: entry,
      remembered: lookupSupplierCode(bill.supplierId, supplierCodeForMatch),
      internalCodePrefix,
    });
    if (result.method === 'created') {
      addProduct({
        code: result.internalCode,
        description: line.description,
        category: 'Uncategorised',
        primarySupplier: bill.supplierId,
        purchasePrice: line.unitPrice,
        markup: 45,
        stock: 0,
        minStock: 0,
      });
    }
    updateBillLine(line.id, {
      ...(sc ? { supplierCode: sc } : {}),
      internalCode: result.internalCode,
      lineType: 'product',
      codeMatch: 'exact',
    });
    if (sc) {
      addSupplierCodeMap({ supplierId: bill.supplierId, supplierCode: sc, internalCode: result.internalCode });
      addSupplierPriceBookEntries([{
        supplierId: bill.supplierId, supplierCode: sc,
        description: line.description, unitPrice: line.unitPrice,
        internalCode: result.internalCode, stocked: false, source: 'manual',
      }]);
    }
  };

  const billNeedsInternal = (line: BillLineItem) =>
    line.lineType !== 'service' && !(line.internalCode?.trim());

  const canInternalAutoCreate = (line: BillLineItem) => {
    if (!line.description?.trim()) return false;
    if (trackSupplierCodes) return !!(line.supplierCode || '').trim();
    return true;
  };
  const nextInternalCodePreview = peekNextInternalCode(products, internalCodePrefix);


  const [tab, setTab] = useState(0);
  const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);

  const [detailsExpanded, setDetailsExpandedRaw] = useState<boolean>(() => {
    try { return localStorage.getItem('bill-details-expanded-v1') === '1'; } catch { return false; }
  });
  const [linesExpanded, setLinesExpandedRaw] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('bill-lines-expanded-v1');
      return v === null ? true : v === '1';
    } catch { return true; }
  });
  const [posExpanded, setPosExpandedRaw] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('bill-pos-expanded-v1');
      return v === null ? true : v === '1';
    } catch { return true; }
  });
  // Session-only object URLs so attached files can be viewed before reload.
  const [attachUrls, setAttachUrls] = useState<Record<string, string>>({});

  const toggleDetailsExpanded = () => {
    const next = !detailsExpanded;
    setDetailsExpandedRaw(next);
    try { localStorage.setItem('bill-details-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const toggleLinesExpanded = () => {
    const next = !linesExpanded;
    setLinesExpandedRaw(next);
    try { localStorage.setItem('bill-lines-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const togglePosExpanded = () => {
    const next = !posExpanded;
    setPosExpandedRaw(next);
    try { localStorage.setItem('bill-pos-expanded-v1', next ? '1' : '0'); } catch {}
  };
  const disclosureKeyDown = (toggle: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
  };

  // Attach a reference file to the bill (name persisted; URL kept for session).
  const handleAttachFile = (file: File) => {
    if (!bill) return;
    const url = URL.createObjectURL(file);
    setAttachUrls(prev => ({ ...prev, [file.name]: url }));
    const next = [...(bill.attachments ?? [])];
    if (!next.includes(file.name)) next.push(file.name);
    updateBill(bill.id, { attachments: next });
  };
  const removeAttachment = (name: string) => {
    if (!bill) return;
    updateBill(bill.id, { attachments: (bill.attachments ?? []).filter(n => n !== name) });
    setAttachUrls(prev => { const n = { ...prev }; delete n[name]; return n; });
  };
  // Hand the dropped invoice to the reconcile view, which owns the AI parser.
  const handleImportBill = (file: File) => {
    if (!bill) return;
    setPendingBillImportFile(file);
    navigate(`/bills/${bill.id}?mode=reconcile`);
  };

  // Approve the bill and/or push it to the accounting product for payment.
  const sendToAccounting = (alsoApprove: boolean) => {
    if (!bill) return;
    const record = buildAccountingExport(accountingProduct, bill.billNo);
    updateBill(bill.id, {
      ...(alsoApprove ? { status: 'Approved' as BillStatus } : {}),
      accountingExport: record,
    });
    alert(
      `${alsoApprove ? 'Approved and sent' : 'Sent'} to ${accountingProduct} for payment.\n` +
      `${accountingProduct} reference: ${record.ref}`,
    );
  };

  if (!bill) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 700, color: '#888', mb: 1 }}>Bill not found</Typography>
        <Button onClick={() => navigate('/bills')} sx={{ textTransform: 'none', color: BRAND }}>
          ← Back to Bills
        </Button>
      </Box>
    );
  }

  const supplierName = supplier?.name ?? '—';
  const createdLine = bill.createdBy
    ? `Created by ${bill.createdBy}${bill.createdAt ? `, ${new Date(bill.createdAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}`
    : '';
  const backToPoPath = linkedPo ? `/po-print/${linkedPo.id}` : null;
  const handleBack = () => navigate(backToPoPath ?? '/bills');
  const backLabel = linkedPo ? `Back to ${linkedPo.id}` : 'Back';

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Page header ──────────────────────────────────────────────── */}
      <Box className="no-print" sx={{ maxWidth: BILL_DOC_MAX_WIDTH, mx: 'auto', width: '100%', flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button startIcon={<ArrowBackIcon />} onClick={handleBack}
              sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#666',
                '&:hover': { backgroundColor: 'transparent', color: BRAND } }}>
              {backLabel}
            </Button>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: FI_ORANGE }}>
              {bill.billNo}
            </Typography>
            <StatusChip
              status={bill.status}
              onClick={(e) => setStatusAnchor(e.currentTarget as HTMLElement)}
              deleteIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
              onDelete={(e) => setStatusAnchor(e.currentTarget as HTMLElement)}
              sx={{ height: 26, cursor: 'pointer' }}
            />
            {bill.accountingExport && (
              <Chip size="small"
                icon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                label={`${bill.accountingExport.product} ${bill.accountingExport.ref}${bill.status === 'Paid' ? ' · paid' : ' · awaiting payment'}`}
                sx={{ height: 24, fontSize: '0.65rem', fontWeight: 800,
                  backgroundColor: bill.status === 'Paid' ? '#e7f6ef' : '#e8f0fe',
                  color: bill.status === 'Paid' ? '#0a6e45' : '#1a56c4',
                  border: `1px solid ${bill.status === 'Paid' ? '#bfe6d2' : '#bcd4f7'}` }} />
            )}
            <Tooltip title="Template" arrow>
              <Select
                size="small"
                value={bill.template ?? 'Default Template'}
                onChange={e => updateBill(bill.id, { template: e.target.value })}
                sx={{ fontSize: '0.72rem', height: 28, minWidth: 160, '& .MuiSelect-select': { py: 0.5 } }}
              >
                {TEMPLATES.map(t => <MenuItem key={t} value={t} sx={{ fontSize: '0.72rem' }}>{t}</MenuItem>)}
              </Select>
            </Tooltip>
          </Stack>
          {createdLine && (
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
              {createdLine}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ borderBottom: '1px solid transparent', mb: 1.5 }}>
          {BILL_TABS.map((label, i) => (
            <Typography key={label}
              onClick={() => setTab(i)}
              sx={{
                fontSize: '0.8rem', fontWeight: i === tab ? 700 : 600,
                color: i === tab ? FI_ORANGE : '#888', pb: 0.6, cursor: 'pointer',
                borderBottom: i === tab ? `2px solid ${FI_ORANGE}` : '2px solid transparent',
                '&:hover': { color: i === tab ? FI_ORANGE : BRAND },
              }}>
              {label}
            </Typography>
          ))}
        </Stack>
      </Box>

      <Menu
        anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={() => setStatusAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 200 } }}
      >
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Set status
          </Typography>
        </Box>
        {BILL_STATUSES.map(s => {
          const tone = STATUS_TONE[getStatusTone(s)];
          const isCurrent = s === bill.status;
          return (
            <MenuItem key={s} selected={isCurrent}
              onClick={() => { updateBill(bill.id, { status: s }); setStatusAnchor(null); }}
              sx={{ py: 0.75, px: 1.5, ...(isCurrent && { backgroundColor: tone.bg }) }}>
              <StatusChip status={s} dense />
            </MenuItem>
          );
        })}
      </Menu>

      <Paper sx={{ maxWidth: BILL_DOC_MAX_WIDTH, mx: 'auto', width: '100%', p: 4,
        border: '1px solid #e0e0e0', backgroundColor: '#fff' }} id="bill-print-area">

        {/* ── Masthead card — purple cascade shell ───────────────────── */}
        <Box className="no-print po-masthead-card" sx={{
          backgroundColor: '#faf8ff',
          border: '1px solid #e0d5f5',
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <Box sx={{ p: 2 }}>
            <Box
              role="button"
              tabIndex={0}
              onClick={toggleDetailsExpanded}
              onKeyDown={disclosureKeyDown(toggleDetailsExpanded)}
              sx={{ cursor: 'pointer', borderRadius: 1, mx: -1, px: 1, py: 0.25,
                '&:hover': { backgroundColor: '#f3edfc' } }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: BRAND,
                  textTransform: 'uppercase', letterSpacing: 1 }}>
                  Supplier Invoice
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.3}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: BRAND }}>
                    Edit details
                  </Typography>
                  <ExpandMoreIcon sx={{
                    fontSize: 18, color: BRAND,
                    transform: detailsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.15s ease',
                  }} />
                </Stack>
              </Stack>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#2a2a2a', mt: 0.3 }}>
                {supplierName}
              </Typography>
              <Stack direction="row" spacing={3.5} mt={1} flexWrap="wrap" useFlexGap>
                {[
                  ['Date', fmtDate(bill.date)],
                  ['Due', fmtDate(bill.dueDate)],
                  ['Invoice #', bill.supplierInvoiceNo || '—'],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#888',
                      textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mt: 0.1 }}>{value}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
          {detailsExpanded && (
            <>
              <Divider sx={{ borderColor: '#e0d5f5' }} />
              <Box sx={{ px: BILL_MASTHEAD_EDIT_INSET, pb: BILL_MASTHEAD_EDIT_INSET, pt: 1.25 }}>
                <BillEditForm bill={bill} />
              </Box>
            </>
          )}
        </Box>

        <Divider className="no-print" sx={{ my: 1.25 }} />

        {/* ── Tab body ───────────────────────────────────────────────── */}
        {tab === 0 ? (
          <>
            {/* ── Line Items (level 1) — import zones + table + totals ───────
                Documents merged here: AI import and attachments sit above the
                line table, matching the PO page pattern (v1.50.1). */}
            <Box className="no-print" sx={{ mb: linesExpanded ? 1.5 : DISCLOSURE_ROW_MB }}>
              <Box
                role="button"
                tabIndex={0}
                onClick={toggleLinesExpanded}
                onKeyDown={disclosureKeyDown(toggleLinesExpanded)}
                sx={{ ...disclosureLevelSx(1), mb: linesExpanded ? DISCLOSURE_ACTION_GAP : DISCLOSURE_ROW_MB }}
              >
                <ExpandMoreIcon sx={{
                  fontSize: 20, color: BRAND, flexShrink: 0,
                  transform: linesExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.15s ease',
                }} />
                <Typography variant="body2" fontWeight={800} sx={{ color: BRAND, flexShrink: 0 }}>
                  Line Items
                </Typography>
                <Chip
                  label={lines.length === 0 ? 'None' : `${lines.length} line${lines.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700, flexShrink: 0,
                    backgroundColor: lines.length > 0 ? '#e8f5e9' : '#f5f5f5',
                    color: lines.length > 0 ? '#2e7d32' : '#888' }}
                />
                <Typography component="span" sx={disclosureSummarySx}>
                  {lines.length > 0 ? (
                    <>
                      <strong style={{ color: BRAND }}>{fmt(bill.totalIncGst)}</strong> inc GST
                      {' · '}<span style={{ color: '#888' }}>{fmt(bill.subtotalExGst)} ex GST</span>
                    </>
                  ) : (bill.attachments?.length ?? 0) > 0 ? (
                    <span style={{ color: '#888' }}>{bill.attachments!.length} attachment{bill.attachments!.length !== 1 ? 's' : ''}</span>
                  ) : (
                    <span style={{ color: '#aaa', fontStyle: 'italic' }}>Import invoice to add lines</span>
                  )}
                </Typography>
              </Box>

              {linesExpanded && (
                <Box>
                  {/* Import + attach — always above the line table (PO pattern) */}
                  <Stack spacing={1.25} sx={{ mb: 2 }}>
                    <AiImportBanner
                      onFile={handleImportBill}
                      title="AI Import bill"
                      subtitle="Drag a supplier invoice here · AI reads the lines, then opens Reconcile against the PO"
                      buttonLabel="Choose invoice"
                    />
                    {(bill.attachments?.length ?? 0) > 0 && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {bill.attachments!.map(name => {
                          const url = attachUrls[name];
                          return (
                            <Chip
                              key={name}
                              icon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
                              label={name}
                              size="small"
                              onClick={url ? () => window.open(url, '_blank', 'noopener') : undefined}
                              onDelete={() => removeAttachment(name)}
                              deleteIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                fontSize: '0.72rem', fontWeight: 600, maxWidth: 280,
                                cursor: url ? 'pointer' : 'default',
                                backgroundColor: '#f3f8fd', border: '1px solid #cfe0f0', color: '#315087',
                              }}
                            />
                          );
                        })}
                      </Stack>
                    )}
                    <AttachFileZone
                      onFile={handleAttachFile}
                      subtitle="Attach a reference document (delivery docket, email) · not parsed into lines"
                    />
                  </Stack>

                  {lines.length === 0 ? (
                    <Box sx={{ py: 2, textAlign: 'center', border: '1px dashed #e0d5f5', borderRadius: 2, backgroundColor: '#faf8ff', mb: 1.5 }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#b0a0d8', fontWeight: 600 }}>No lines yet</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: '#ccc', mt: 0.3 }}>
                        Drop a supplier invoice above, or use Reconcile in the footer below to match against the PO
                      </Typography>
                    </Box>
                  ) : (
                    <Table size="small" sx={{ mb: 1.5 }}>
                      <TableHead>
                        <TableRow>
                          {showCol('type') && (
                            <TableCell sx={TABLE_HEAD_CELL}>Type</TableCell>
                          )}
                          {trackSupplierCodes && showCol('supplierCode') && (
                            <TableCell sx={{ ...TABLE_HEAD_SUPPLIER, minWidth: 128 }}>Supplier Code</TableCell>
                          )}
                          {showCol('internalCode') && (
                            <TableCell sx={{ ...TABLE_HEAD_CELL, minWidth: 120 }}>Inventory Code</TableCell>
                          )}
                          <TableCell sx={{ ...TABLE_HEAD_CELL, minWidth: 220 }}>Description</TableCell>
                          <TableCell sx={{ ...TABLE_HEAD_CELL, minWidth: 52 }}>Qty</TableCell>
                          {showCol('unitPrice') && (
                            <TableCell sx={{ ...TABLE_HEAD_CELL, minWidth: 88 }}>Unit Price</TableCell>
                          )}
                          <TableCell sx={{ ...TABLE_HEAD_CELL, minWidth: 92 }}>Amount</TableCell>
                          <LineTableColumnSettings
                            columns={BILL_LINE_COLUMN_DEFS}
                            visible={colVisible}
                            onToggle={toggleCol}
                            documentKind="bill"
                            templateName={bill?.template}
                            returnTo={bill ? `/bills/${bill.id}` : undefined}
                          />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lines.map(line => (
                          <TableRow key={line.id} hover>
                            {showCol('type') && (
                            <TableCell sx={{ fontSize: '0.76rem', fontWeight: 600, textTransform: 'capitalize' }}>
                              {line.lineType ?? 'product'}
                            </TableCell>
                            )}
                            {trackSupplierCodes && showCol('supplierCode') && (
                            <TableCell sx={{ minWidth: 150 }}>
                              <LineCodeSelect
                                value={line.supplierCode ?? ''}
                                options={supplierOptions}
                                onPick={opt => {
                                  const entry = bookForSupplier.find(e => e.id === opt.key);
                                  updateBillLine(line.id, {
                                    supplierCode: opt.code,
                                    ...(entry?.internalCode ? { internalCode: entry.internalCode, codeMatch: 'exact' as const } : {}),
                                    ...(entry?.description && !line.description ? { description: entry.description } : {}),
                                    ...(entry?.unitPrice && !line.unitPrice ? { unitPrice: entry.unitPrice } : {}),
                                  });
                                }}
                                onCreateNew={() => alert('Add supplier code — use price book or type the code from the invoice.')}
                                onFreeText={t => updateBillLine(line.id, { supplierCode: t || '' })}
                                newLabel="supplier code"
                                accent="#6a1b9a"
                                uppercase
                                emptyLabel="Add supplier code"
                                placeholder="Search supplier codes…"
                              />
                            </TableCell>
                            )}
                            {showCol('internalCode') && (
                            <TableCell sx={{ minWidth: 150 }}>
                              {line.lineType === 'service' ? (
                                <Typography sx={{ fontSize: '0.75rem', color: '#bbb' }}>—</Typography>
                              ) : (
                                <LineCodeSelect
                                  value={line.internalCode ?? ''}
                                  options={internalOptions}
                                  onPick={opt => updateBillLine(line.id, { internalCode: opt.code, codeMatch: 'exact', lineType: 'product' })}
                                  onCreateNew={() => alert('Create inventory item — backlog.')}
                                  onAutoCreate={canInternalAutoCreate(line) ? () => handleInternalAutoCreate(line) : undefined}
                                  autoCreateLabel={`Auto-set ${nextInternalCodePreview}`}
                                  autoCreatePreview={nextInternalCodePreview}
                                  onFreeText={t => updateBillLine(line.id, { internalCode: t, lineType: 'product' })}
                                  newLabel="inventory item"
                                  accent={FI_ORANGE}
                                  uppercase
                                  needsAction={billNeedsInternal(line)}
                                  actionLabel="Select code"
                                  placeholder="Search inventory…"
                                />
                              )}
                            </TableCell>
                            )}
                            <TableCell sx={{ fontSize: '0.78rem' }}>
                              <TableCellInput
                                value={line.description}
                                placeholder="Add description"
                                fontWeight={400}
                                fontSize="0.78rem"
                                onCommit={v => updateBillLine(line.id, { description: v.trim() })}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.76rem', textAlign: 'right' }}>
                              <TableCellInput
                                value={String(line.qty)}
                                numeric
                                align="center"
                                width={44}
                                fontSize="0.75rem"
                                aria-label="Quantity"
                                onCommit={v => {
                                  const n = parseFloat(v);
                                  if (!isNaN(n) && n >= 0) updateBillLine(line.id, { qty: n });
                                }}
                              />
                            </TableCell>
                            {showCol('unitPrice') && (
                            <TableCell sx={{ fontSize: '0.76rem', textAlign: 'right' }}>
                              <TableCellInput
                                value={line.unitPrice.toFixed(2)}
                                numeric
                                align="right"
                                width={72}
                                fontSize="0.75rem"
                                aria-label="Unit price"
                                onCommit={v => {
                                  const n = parseFloat(v.replace(/[$,]/g, ''));
                                  if (!isNaN(n) && n >= 0) updateBillLine(line.id, { unitPrice: n });
                                }}
                              />
                            </TableCell>
                            )}
                            <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, textAlign: 'right' }}>
                              {(line.lineTotal ?? line.qty * line.unitPrice).toFixed(2)}
                            </TableCell>
                            <TableCell className="no-print" sx={{ width: 40, borderBottom: '1px solid #e0e0e0', p: 0.25 }}>
                              <Tooltip title="Remove line" arrow>
                                <IconButton size="small" onClick={() => handleDeleteLine(line.id)}
                                  sx={{ p: 0.4, color: '#ccc', '&:hover': { color: '#c62828' } }}>
                                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}

                  {/* + Add line item — PO parity (manual entry on the bill) */}
                  <Stack direction="row" className="no-print" sx={{ mb: 0.75 }}>
                    <Button size="small" variant="text" onClick={handleAddLineItem}
                      sx={{ textTransform: 'none', fontSize: '0.74rem', fontWeight: 700, color: '#027BFF',
                        px: 0.75, '&:hover': { backgroundColor: '#f0f7ff' } }}>
                      + Add line item
                    </Button>
                  </Stack>

                  {lines.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 0.5 }}>
                      <Paper sx={{ p: 2, minWidth: 280, border: '1px solid #e8e2f4', backgroundColor: '#faf9fd' }}>
                        {[
                          ['Subtotal (Ex GST)', bill.subtotalExGst],
                          ['GST', bill.totalGst],
                        ].map(([l, v]) => (
                          <Stack key={l as string} direction="row" justifyContent="space-between" mb={0.5}>
                            <Typography variant="caption" color="text.secondary">{l}</Typography>
                            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {(v as number).toFixed(2)}
                            </Typography>
                          </Stack>
                        ))}
                        <Divider sx={{ my: 0.5 }} />
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" fontWeight={800}>TOTAL (inc GST)</Typography>
                          <Typography variant="body2" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {bill.totalIncGst.toFixed(2)}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Divider className="no-print" sx={{ my: 1.25 }} />

            {/* ── Purchase Orders (level 2) — below line items ─────────────── */}
            <Box className="no-print" sx={{ mb: posExpanded ? 1.5 : DISCLOSURE_ROW_MB }}>
              <Box
                role="button"
                tabIndex={0}
                onClick={togglePosExpanded}
                onKeyDown={disclosureKeyDown(togglePosExpanded)}
                sx={{ ...disclosureLevelSx(2), mb: posExpanded ? DISCLOSURE_ACTION_GAP : DISCLOSURE_ROW_MB }}
              >
                <ExpandMoreIcon sx={{
                  fontSize: 20, color: '#6a4fc4', flexShrink: 0,
                  transform: posExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.15s ease',
                }} />
                <Typography variant="body2" fontWeight={800} sx={{ color: '#5a3db0', flexShrink: 0 }}>
                  Purchase Orders
                </Typography>
                <Chip
                  label={linkedPo ? '1 PO' : 'None'}
                  size="small"
                  sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700, flexShrink: 0,
                    backgroundColor: linkedPo ? '#e8f5e9' : '#f5f5f5',
                    color: linkedPo ? '#2e7d32' : '#888' }}
                />
                <Typography component="span" sx={disclosureSummarySx}>
                  {linkedPo ? (
                    <>
                      <strong style={{ color: BRAND }}>{linkedPo.id}</strong>
                      {' · '}<span style={{ color: '#888' }}>{fmt(linkedPo.total)} inc GST</span>
                    </>
                  ) : (
                    <span style={{ color: '#aaa', fontStyle: 'italic' }}>No PO linked</span>
                  )}
                </Typography>
              </Box>

              {posExpanded && (
                <Stack direction="row" spacing={0.8} alignItems="center" mb={1.2} flexWrap="wrap" useFlexGap>
                  <InfoableButton info={FOOTER_BTN_INFO.findPo}>
                    <Button size="small" variant="contained" disableElevation
                      onClick={() => alert('Find PO — backlog')}
                      sx={BTN_STANDARD}>
                      + Find PO
                    </Button>
                  </InfoableButton>
                  <InfoableButton info={FOOTER_BTN_INFO.generatePoFromBill}>
                    <Button size="small" variant="contained" disableElevation
                      onClick={() => alert('Generate PO from Bill — backlog')}
                      sx={BTN_STANDARD}>
                      + Generate PO from Bill
                    </Button>
                  </InfoableButton>
                  <InfoableButton info={FOOTER_BTN_INFO.generateBlankPo}>
                    <Button size="small" variant="contained" disableElevation
                      onClick={() => alert('Generate Blank PO — backlog')}
                      sx={BTN_STANDARD}>
                      + Generate Blank PO
                    </Button>
                  </InfoableButton>
                </Stack>
              )}

              {posExpanded && (!linkedPo ? (
                <Box sx={{ py: 2, textAlign: 'center', border: '1px dashed #e0d5f5', borderRadius: 2, backgroundColor: '#faf8ff' }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#b0a0d8', fontWeight: 600 }}>No purchase order linked to this bill yet</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#ccc', mt: 0.3 }}>
                    Link an existing PO or generate one from the buttons above
                  </Typography>
                </Box>
              ) : (
                <Table size="small" sx={{ '& td, & th': { fontSize: '0.72rem' }, mb: 1.5 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
                      {['Number', 'Status', 'Date', 'Total'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, color: BRAND, fontSize: '0.68rem', py: 0.8 }}>{h}</TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 700, color: BRAND, fontSize: '0.68rem', py: 0.8 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/po-print/${linkedPo.id}`)}>
                      <TableCell sx={{ fontWeight: 700, color: BRAND }}>{linkedPo.id}</TableCell>
                      <TableCell><StatusChip status={linkedPo.status} dense /></TableCell>
                      <TableCell>{fmtDate(linkedPo.date)}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{fmt(linkedPo.total)}</TableCell>
                      <TableCell>
                        <Tooltip title="Open PO">
                          <Button size="small" variant="outlined"
                            startIcon={<OpenInNewIcon sx={{ fontSize: 11 }} />}
                            onClick={e => { e.stopPropagation(); navigate(`/po-print/${linkedPo.id}`); }}
                            sx={{ textTransform: 'none', fontSize: '0.65rem', borderColor: BRAND, color: BRAND, py: 0.2, px: 0.8, minWidth: 0 }}>
                            Open
                          </Button>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              ))}
            </Box>
          </>
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.82rem', color: '#aaa' }}>No history entries yet.</Typography>
          </Box>
        )}

        {/* Version lives in app Layout footer only — no mid-page prototype stamp. */}

        {/* ── Bottom action bar — doc tools scroll; lifecycle sticky below ─ */}
        <Box className="no-print" sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #e0e0e0' }}>
          <Stack direction="row" spacing={0.8} alignItems="center" mb={1} flexWrap="wrap" useFlexGap>
            <InfoableButton info={FOOTER_BTN_INFO.preview}>
              <Button size="small" disableElevation startIcon={<VisibilityIcon />}
                onClick={() => alert('Preview bill — backlog')}
                sx={BTN_GHOST_DOC}>Preview</Button>
            </InfoableButton>
            <InfoableButton info={FOOTER_BTN_INFO.pdf}>
              <Button size="small" disableElevation startIcon={<PictureAsPdfIcon />}
                onClick={() => window.print()} sx={BTN_GHOST_DOC}>PDF</Button>
            </InfoableButton>
            <InfoableButton info={bill.accountingExport ? FOOTER_BTN_INFO.resend : FOOTER_BTN_INFO.exportXero}>
              <Button size="small" disableElevation startIcon={<OpenInNewIcon />}
                onClick={() => sendToAccounting(false)} sx={BTN_GHOST_DOC}>
                {bill.accountingExport ? `Re-send to ${accountingProduct}` : `Send to ${accountingProduct}`}
              </Button>
            </InfoableButton>
            <InfoableButton info={FOOTER_BTN_INFO.reconcile}>
              <Button size="small" variant="contained" disableElevation
                startIcon={<CompareArrowsIcon sx={{ fontSize: 14 }} />}
                onClick={() => navigate(`/bills/${bill.id}?mode=reconcile`)}
                sx={linkedPo ? BTN_PURPLE : BTN_GHOST_DOC}>
                Reconcile{linkedPo ? ` · ${linkedPo.id}` : ''}
              </Button>
            </InfoableButton>
          </Stack>
        </Box>

        {/* Row 2 — Lifecycle (sticky: Close · primary · Delete) */}
        <Box className="no-print po-sticky-nav" sx={PO_STICKY_LIFECYCLE_SX}>
            <InfoableButton info={FOOTER_BTN_INFO.close}>
              <Button size="small" disableElevation
                onClick={handleBack}
                sx={BTN_STANDARD}>{linkedPo ? `Close · ${linkedPo.id}` : 'Close'}</Button>
            </InfoableButton>

            <Box sx={{ width: 24, flexShrink: 0 }} />

            {bill.status === 'Draft' && (
              <InfoableButton info={FOOTER_BTN_INFO.submitApproval}>
                <Button size="small" variant="contained" disableElevation
                  onClick={() => updateBill(bill.id, { status: 'Awaiting approval' })}
                  sx={BTN_STATUS_GREEN}>Submit for Approval</Button>
              </InfoableButton>
            )}
            {bill.status === 'Awaiting approval' && (
              <InfoableButton info={FOOTER_BTN_INFO.approveSend}>
                <Button size="small" variant="contained" disableElevation
                  startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  onClick={() => sendToAccounting(true)}
                  sx={BTN_STATUS_GREEN}>{`Approve & send to ${accountingProduct}`}</Button>
              </InfoableButton>
            )}
            {bill.status === 'Approved' && !bill.accountingExport && (
              <InfoableButton info={FOOTER_BTN_INFO.sendPayment}>
                <Button size="small" variant="contained" disableElevation
                  startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  onClick={() => sendToAccounting(false)}
                  sx={BTN_STATUS_GREEN}>{`Send to ${accountingProduct} for payment`}</Button>
              </InfoableButton>
            )}
            {bill.status === 'Approved' && bill.accountingExport && (
              <InfoableButton info={FOOTER_BTN_INFO.markPaid}>
                <Button size="small" variant="contained" disableElevation
                  onClick={() => updateBill(bill.id, { status: 'Paid' })}
                  sx={BTN_STATUS_GREEN}>Mark as Paid</Button>
              </InfoableButton>
            )}
            <Box sx={PO_STICKY_LIFECYCLE_PUSH} />

            <Box sx={PO_STICKY_LIFECYCLE_TRAIL}>
              <InfoableButton info={FOOTER_BTN_INFO.deleteBill}>
                <Button size="small" disableElevation startIcon={<DeleteOutlineIcon sx={{ fontSize: 13 }} />}
                  onClick={() => {
                    const msg = bill.status === 'Draft'
                      ? `Delete ${bill.billNo}? This cannot be undone.`
                      : `Delete ${bill.billNo}? This bill is ${bill.status}. Deleting is permanent.`;
                    if (window.confirm(msg)) {
                      deleteBillImport(bill.id);
                      navigate('/bills');
                    }
                  }}
                  sx={BTN_GHOST_DANGER}>Delete</Button>
              </InfoableButton>
            </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default BillDocumentView;
